import {
  LaunchPage,
  sortByDateTime,
  TableTagBadge,
  MaterialUiTable,
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  Spacer,
  Tag,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectBadgesList, selectBadgesListStatus } from "./badgesSlice";
import { BadgesList, BadgesListDisplayed, BadgeStatus } from "./badgesAPI";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import styled from "styled-components";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import "../../../../i18n/i18n";

interface SharedInterfaceBadgesProps {
  sidePanelAction?: (props: Partial<BadgesList>) => void;
  sidePanel?: JSX.Element;
}

export const SharedInterfaceBadges = (props: SharedInterfaceBadgesProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [listDisplayed, setListDisplayed] = useState<
    BadgesListDisplayed[] | null
  >(null);
  const badgesListInit = useAppSelector(selectBadgesList);
  const badgesListStatus = useAppSelector(selectBadgesListStatus);

  const pageName = "badges";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      number: "size",
      status: "size",
      reference: "size",
      registrationNumber: "size",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof BadgesList, value: string) => {
      switch (key) {
        case "status":
          return t(`badges.status.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["status"] as (keyof BadgesListDisplayed)[],
    [],
  );

  const badgesList: BadgesList[] = useMemo(() => {
    const rowsWithId = badgesListInit.map((av) => {
      let newDict: BadgesList & { id: string } = {
        ...av,
        id: av.uuid,
      };
      Object.entries(av).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof BadgesListDisplayed)) {
          newDict = {
            ...newDict,
            [key]: value
              ? findKeysToTranslate(key as keyof BadgesList, value as string)
              : "",
          };
        }
      });
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [badgesListInit, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (badgesList && badgesListStatus === "success") {
      const badgesDateTimeSorted = sortByDateTime(badgesList, "desc");
      setListDisplayed(badgesDateTimeSorted);
    }
  }, [badgesList, badgesListStatus]);

  const searchBarProps = {
    values: badgesList,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const, "date" as const, "vehicleUuid" as const],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("badges.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof BadgesListDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      number: {
        type: "string",
      },
      status: {
        type: "string",
        valueFormatter: (value: BadgeStatus) =>
          value && (
            <StyledRow>
              <TableTagBadge status={value} />
            </StyledRow>
          ),
      },
      reference: {
        type: "string",
        width: 250,
        valueFormatter: (value) =>
          value && (
            <StyledRow>
              <div style={{ width: "2rem" }}>
                <img
                  src={Car}
                  style={{ width: "100%", filter: "grayscale(100%)" }}
                  alt="Car"
                />
              </div>
              <Spacer x={0.5} />
              <Tag
                textColor={colors["colors/text/black"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                text={value}
                borderColor={colors["colors/borders/cells/cells"]}
              />
            </StyledRow>
          ),
      },
      registrationNumber: {
        type: "string",
      },
      vehicleAnalyticCode: {
        type: "string",
        width: 200,
      },
    };
  }, []);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <>
      <MaterialUiTable<keyof BadgesListDisplayed, BadgesListDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        onRowClickAction={props.sidePanelAction}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
      />
      {props.sidePanel ? props.sidePanel : <></>}
    </>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
